import React, { useState } from "react";
import { Box, Container, Heading, Text, VStack, FormControl, FormLabel, Input, Select, Button, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText } from "@chakra-ui/react";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const FinanceToolsSMSF: React.FC = () => {
  const [superBalance, setSuperBalance] = useState(250000);
  const [contribution, setContribution] = useState(15000);
  const [propertyValue, setPropertyValue] = useState(500000);
  const [deposit, setDeposit] = useState(125000);
  const [interestRate, setInterestRate] = useState(6.8);
  const loanAmount = propertyValue - deposit;
  const monthlyRepayment = (loanAmount * (interestRate / 100 / 12)) / (1 - Math.pow(1 + (interestRate / 100 / 12), -300));
  const taxSaving = contribution * 0.15;

  return (
    <Box minH="100vh" bg="white">
      <SEOHead pagePath="/finance/tools/smsf" fallbackTitle="SMSF Calculator" fallbackDescription="Calculate SMSF contributions and tax benefits" />
      <NavbarWithCallToAction />
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">Finance Section</Text>
            <Heading size="2xl" fontWeight="bold">SMSF Calculator</Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">Calculate SMSF loan capacity</Text>
          </VStack>
        </Container>
      </Box>
      <Container maxW="container.xl" py={20} flex="1">
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10}>
          <Box bg="white" p={8} borderRadius="xl" border="1px solid" borderColor="gray.200" shadow="lg">
            <Heading size="md" mb={6} color="purple.800">SMSF Details</Heading>
            <VStack spacing={6} align="stretch">
              <FormControl>
                <FormLabel>Current Super Balance</FormLabel>
                <Input type="number" value={superBalance} onChange={(e) => setSuperBalance(Number(e.target.value))} size="lg" />
                <Text fontSize="sm" color="gray.600" mt={1}>${superBalance.toLocaleString()}</Text>
              </FormControl>
              <FormControl>
                <FormLabel>Annual Contribution</FormLabel>
                <Input type="number" value={contribution} onChange={(e) => setContribution(Number(e.target.value))} size="lg" />
                <Text fontSize="sm" color="gray.600" mt={1}>Max concessional: $30,000</Text>
              </FormControl>
              <FormControl>
                <FormLabel>Property Value</FormLabel>
                <Input type="number" value={propertyValue} onChange={(e) => setPropertyValue(Number(e.target.value))} size="lg" />
              </FormControl>
              <FormControl>
                <FormLabel>Deposit Amount (Min 25%)</FormLabel>
                <Input type="number" value={deposit} onChange={(e) => setDeposit(Number(e.target.value))} size="lg" />
              </FormControl>
              <FormControl>
                <FormLabel>Interest Rate (%)</FormLabel>
                <Input type="number" step="0.1" value={interestRate} onChange={(e) => setInterestRate(Number(e.target.value))} size="lg" />
              </FormControl>
              <Button colorScheme="purple" size="lg">Calculate SMSF Capacity</Button>
            </VStack>
          </Box>
          <Box>
            <Box bg="gradient-to-br" bgGradient="linear(to-br, purple.500, pink.600)" p={8} borderRadius="xl" color="white" textAlign="center" mb={6}>
              <Text fontSize="sm" textTransform="uppercase" mb={2}>SMSF Loan Amount</Text>
              <Heading size="3xl" mb={2}>${loanAmount.toLocaleString()}</Heading>
              <Text fontSize="sm">For property investment</Text>
            </Box>
            <SimpleGrid columns={2} spacing={4} mb={6}>
              <Stat bg="purple.50" p={4} borderRadius="lg">
                <StatLabel>Monthly Repayment</StatLabel>
                <StatNumber fontSize="xl">${Math.round(monthlyRepayment).toLocaleString()}</StatNumber>
                <StatHelpText>at {interestRate}%</StatHelpText>
              </Stat>
              <Stat bg="green.50" p={4} borderRadius="lg">
                <StatLabel>Tax Benefit</StatLabel>
                <StatNumber fontSize="xl">${taxSaving.toLocaleString()}</StatNumber>
                <StatHelpText>15% tax in super</StatHelpText>
              </Stat>
            </SimpleGrid>
            <Box bg="purple.50" p={6} borderRadius="lg" border="1px solid" borderColor="purple.200">
              <Heading size="sm" mb={3}>SMSF Requirements</Heading>
              <VStack align="start" spacing={2} fontSize="sm">
                <Text>• Minimum 25% deposit required for SMSF loans</Text>
                <Text>• Property must be held for investment only</Text>
                <Text>• Rental income goes to super fund (taxed at 15%)</Text>
                <Text>• Cannot live in or rent to related parties</Text>
                <Text>• Limited Recourse Borrowing Arrangement (LRBA)</Text>
              </VStack>
            </Box>
          </Box>
        </SimpleGrid>
      </Container>
      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceToolsSMSF;
